import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Check, Search, Building2, CreditCard, Sparkles,
  ShieldCheck, Lock, Loader2, Trophy, Wallet, Calendar, AlertCircle, ChevronDown,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { CreditCardVisual } from '@/components/cards/CreditCardVisual';
import { Cards as CardsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatINR } from '@/lib/format';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const DESIGN_PREVIEW = {
  'silver-metallic': 'linear-gradient(135deg,#d8d9dd,#a3a5ab 45%,#d2d4d8 55%,#8c8e93)',
  'navy-stars': 'linear-gradient(135deg,#07112b,#0e1c44)',
  'emerald-luxury': 'linear-gradient(135deg,#082f22,#0c4533)',
  'modern-teal': 'linear-gradient(135deg,#0a2b30,#0e3f44)',
  'graphite-gold': 'linear-gradient(135deg,#1a1c20,#0c0d10)',
};

const StepHeader = ({ n, total, label, active }) => (
  <div className={cn('flex items-center gap-2 text-xs', active ? 'text-foreground' : 'text-muted-foreground')}>
    <span className={cn(
      'h-6 w-6 rounded-full grid place-items-center text-[11px] font-display font-semibold border',
      active ? 'border-accent bg-accent/10 text-accent' : 'border-border bg-[hsl(var(--surface-2))]'
    )}>{n}</span>
    <span className="uppercase tracking-[0.16em]">{label}</span>
    {n < total && <ChevronRight className="h-3 w-3 text-muted-foreground/60" />}
  </div>
);

export default function AddCard() {
  const navigate = useNavigate();
  const { user, demoLogin } = useAuth();

  const [step, setStep] = useState(1);
  const [banks, setBanks] = useState([]);
  const [bankQuery, setBankQuery] = useState('');
  const [bank, setBank] = useState(null);

  const [cardsForBank, setCardsForBank] = useState([]);
  const [cardQuery, setCardQuery] = useState('');
  const [card, setCard] = useState(null);
  const [loadingCards, setLoadingCards] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(true);

  // User-only fields
  const [nickname, setNickname] = useState('');
  const [approvedAt, setApprovedAt] = useState('');
  // Phase 10.2 — Approval date is required. If the user doesn't know, they
  // tick "Unknown — assume today" which stamps `today` into `approvedAt`
  // and unblocks the wizard while warning that historic milestones won't
  // be tracked retroactively.
  const [approvedAtUnknown, setApprovedAtUnknown] = useState(false);
  const [creditLimit, setCreditLimit] = useState('');
  const [last4, setLast4] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Phase 4.G — capture the user's CURRENT state on the card so the engine
  // doesn't re-credit benefits they've already earned (e.g. an Amex Platinum
  // Travel Taj voucher that fired last anniversary).
  const [currentRewardPoints, setCurrentRewardPoints] = useState('');
  const [achievedMilestoneIds, setAchievedMilestoneIds] = useState([]);
  const [achievedTierId, setAchievedTierId] = useState('');
  const [tierProgressInr, setTierProgressInr] = useState('');
  // Phase 10.2 — "Current State" is now MANDATORY. The user must either
  // tick "I've earned nothing yet" (the fast path for fresh cards) or
  // actually fill in the fields below. The section is auto-expanded.
  const [showCurrentState, setShowCurrentState] = useState(true);
  const [earnedNothingYet, setEarnedNothingYet] = useState(false);

  // When the user picks a new card, reset state-capture inputs (they're
  // card-specific — milestone ids from card A won't apply to card B).
  useEffect(() => {
    setAchievedMilestoneIds([]);
    setAchievedTierId('');
    setTierProgressInr('');
    setCurrentRewardPoints('');
    setEarnedNothingYet(false);
    setShowCurrentState(true);
  }, [card?.id]);

  const [saving, setSaving] = useState(false);
  const [walletIds, setWalletIds] = useState([]);

  // Load banks + user's existing wallet
  useEffect(() => {
    const run = async () => {
      try { if (!user) await demoLogin(); } catch (_) {}
      try {
        const [b, mine] = await Promise.all([
          CardsApi.banks(),
          CardsApi.mine().catch(() => []),
        ]);
        setBanks(b || []);
        setWalletIds((mine || []).map((c) => c.id));
      } finally { setLoadingBanks(false); }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // When bank chosen, fetch cards for that bank
  useEffect(() => {
    if (!bank) return;
    setLoadingCards(true);
    CardsApi.library({ bank })
      .then((d) => setCardsForBank(d || []))
      .finally(() => setLoadingCards(false));
  }, [bank]);

  const filteredBanks = useMemo(() => {
    if (!bankQuery) return banks;
    const q = bankQuery.toLowerCase();
    return banks.filter((b) => b.bank.toLowerCase().includes(q));
  }, [banks, bankQuery]);

  const filteredCards = useMemo(() => {
    if (!cardQuery) return cardsForBank;
    const q = cardQuery.toLowerCase();
    return cardsForBank.filter((c) => `${c.name} ${c.tier}`.toLowerCase().includes(q));
  }, [cardsForBank, cardQuery]);

  const alreadyOwned = card && walletIds.includes(card.id);

  // Phase 14 — joining date is now **optional**. Users can either pick a
  // confirmed date or skip it entirely (we'll mark the card as
  // "estimate" and lock anniversary/welcome milestones until they confirm
  // a date later from the Milestones / Card detail surfaces).
  //
  // `approvedAtUnknown` is preserved for backward compatibility but is no
  // longer required to submit; toggling it just clears the date field.
  const currentStateSatisfied =
    earnedNothingYet ||
    (currentRewardPoints !== '' && currentRewardPoints !== null) ||
    achievedMilestoneIds.length > 0 ||
    !!achievedTierId ||
    tierProgressInr !== '';
  const canSubmit = !!card && !alreadyOwned && currentStateSatisfied;
  const submitBlockReason = !card
    ? null
    : !currentStateSatisfied
      ? 'Tell us your current state on this card (or tick "Earned nothing yet").'
      : null;

  const goToStep = (s) => {
    if (s === 1) { setBank(null); setCard(null); }
    if (s === 2) { setCard(null); }
    setStep(s);
  };

  const submit = async () => {
    if (!card) return;
    if (alreadyOwned) { toast.error('This card is already in your wallet'); return; }
    if (!currentStateSatisfied) {
      toast.error('Confirm your current state on this card (tick "Earned nothing yet" if applicable).');
      return;
    }
    setSaving(true);
    // Phase 14 — if user leaves date blank OR ticks "don't remember", send
    // null. Backend will mark `joining_date_is_estimate=true` and lock
    // anniversary/welcome milestones until the user confirms a date later.
    const effectiveApprovedAt = approvedAtUnknown ? null : (approvedAt || null);
    try {
      await CardsApi.add({
        card_id: card.id,
        nickname: nickname.trim() || null,
        approved_at: effectiveApprovedAt,
        credit_limit_inr: creditLimit ? Number(creditLimit) : null,
        last4: last4 && /^\d{4}$/.test(last4) ? last4 : null,
        is_active: isActive,
        // Phase 4.G — current state. If "Earned nothing yet" is ticked we
        // explicitly send zero / empty so the engine starts from a clean
        // baseline.
        current_reward_points: earnedNothingYet ? 0 : (currentRewardPoints ? Number(currentRewardPoints) : 0),
        achieved_milestone_ids: earnedNothingYet ? [] : achievedMilestoneIds,
        achieved_tier_id: earnedNothingYet ? null : (achievedTierId || null),
        tier_progress_inr: earnedNothingYet ? 0 : (tierProgressInr ? Number(tierProgressInr) : 0),
      });
      toast.success(`${card.bank} — ${card.name} added to your wallet.`);
      navigate('/cards');
    } catch (e) {
      toast.error(e.message || 'Could not add card');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/cards')} className="gap-1.5 -ml-2" data-testid="addcard-back">
          <ChevronLeft className="h-4 w-4" /> Back to cards
        </Button>
        <div className="mt-2 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-accent font-medium">Add to wallet</div>
            <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Add a card</h1>
            <p className="mt-1 text-sm text-muted-foreground max-w-xl">
              Pick from our <b className="text-foreground">canonical card library</b>. Reward rules, milestones, and fees auto-populate — you never enter them manually.
            </p>
          </div>
          <Badge variant="outline" className="gap-1.5"><ShieldCheck className="h-3 w-3 text-emerald-500" /> Platform-owned reward data</Badge>
        </div>
      </div>

      {/* Stepper */}
      <Card className="p-3 flex items-center gap-4 flex-wrap">
        <button onClick={() => goToStep(1)} className="flex items-center gap-2"><StepHeader n={1} total={3} label="Bank" active={step >= 1} /></button>
        <button onClick={() => bank && goToStep(2)} className="flex items-center gap-2" disabled={!bank}><StepHeader n={2} total={3} label="Card" active={step >= 2} /></button>
        <button onClick={() => card && goToStep(3)} className="flex items-center gap-2" disabled={!card}><StepHeader n={3} total={3} label="Your details" active={step >= 3} /></button>
        <div className="ml-auto text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" />
          {step === 1 && 'No reward data entered — we look it up for you.'}
          {step === 2 && `${cardsForBank.length} cards in ${bank} library`}
          {step === 3 && 'Only your personal fields below.'}
        </div>
      </Card>

      {/* STEP 1: Bank */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="step1" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }}>
            <Card className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-sm font-medium"><Building2 className="h-4 w-4 text-accent" /> Choose your bank</div>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={bankQuery} onChange={(e) => setBankQuery(e.target.value)} placeholder="Search banks..." className="pl-10 h-10 border-transparent bg-[hsl(var(--surface-2))]" data-testid="addcard-bank-search" />
                </div>
              </div>
              {loadingBanks ? (
                <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : (
                <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="addcard-bank-grid">
                  {filteredBanks.map((b) => (
                    <motion.button
                      key={b.bank}
                      whileHover={{ y: -2 }}
                      onClick={() => { setBank(b.bank); setStep(2); }}
                      data-testid={`addcard-bank-tile-${b.bank.replace(/\s+/g, '-').toLowerCase()}`}
                      className="text-left rounded-xl border border-border bg-[hsl(var(--surface-2))] hover:border-accent/40 hover:bg-accent/5 transition-colors p-4 flex items-center gap-3 relative overflow-hidden"
                    >
                      <div className="h-11 w-11 rounded-xl text-white grid place-items-center font-display font-semibold shrink-0 shadow-sm" style={{ background: DESIGN_PREVIEW[b.sample_design] || DESIGN_PREVIEW['graphite-gold'] }}>
                        {b.bank.split(' ').map((w) => w[0]).slice(0, 2).join('')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-sm font-semibold truncate">{b.bank}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{b.count} {b.count === 1 ? 'card' : 'cards'} available</div>
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </motion.button>
                  ))}
                  {filteredBanks.length === 0 && (
                    <div className="col-span-full py-10 text-center text-sm text-muted-foreground">
                      No banks match. We currently support 8 issuers; more being added weekly.
                    </div>
                  )}
                </div>
              )}
              <div className="mt-5 rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground flex items-start gap-2">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>Don&rsquo;t see your bank? We add new banks weekly. Tell us at <a href="mailto:info@cardassist.online" className="underline">info@cardassist.online</a>.</span>
              </div>
            </Card>
          </motion.div>
        )}

        {/* STEP 2: Card */}
        {step === 2 && bank && (
          <motion.div key="step2" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }}>
            <Card className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => goToStep(1)} className="gap-1.5 -ml-2"><ChevronLeft className="h-3.5 w-3.5" /> Banks</Button>
                  <Separator orientation="vertical" className="h-5" />
                  <div className="text-sm font-medium flex items-center gap-2"><CreditCard className="h-4 w-4 text-accent" /> {bank}</div>
                </div>
                <div className="relative w-full sm:w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={cardQuery} onChange={(e) => setCardQuery(e.target.value)} placeholder="Search cards in this bank..." className="pl-10 h-10 border-transparent bg-[hsl(var(--surface-2))]" data-testid="addcard-card-search" />
                </div>
              </div>
              {loadingCards ? (
                <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
              ) : (
                <div className="mt-5 space-y-2" data-testid="addcard-card-list">
                  {filteredCards.map((c) => {
                    const owned = walletIds.includes(c.id);
                    return (
                      <motion.button
                        key={c.id}
                        whileHover={{ x: 2 }}
                        onClick={() => { if (owned) return; setCard(c); setStep(3); }}
                        disabled={owned}
                        data-testid={`addcard-card-row-${c.id}`}
                        className={cn(
                          'w-full text-left rounded-xl border p-4 flex items-center gap-4 transition-colors',
                          owned
                            ? 'border-border bg-[hsl(var(--surface-2))] opacity-70 cursor-not-allowed'
                            : 'border-border bg-[hsl(var(--surface-2))] hover:border-accent/40 hover:bg-accent/5'
                        )}
                      >
                        <div className="h-10 w-14 rounded-lg shrink-0 shadow-sm relative overflow-hidden" style={{ background: DESIGN_PREVIEW[c.design] || DESIGN_PREVIEW['graphite-gold'] }}>
                          <div className="absolute top-1 left-1 right-1 h-1 rounded-sm bg-white/15" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-display text-base font-semibold">{c.name}</div>
                            <Badge variant="outline" className="text-[10px]">{c.tier}</Badge>
                            <Badge variant="outline" className="text-[10px]">{c.network}</Badge>
                            {owned && <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/30">In wallet</Badge>}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground line-clamp-1">
                            Best for: {(c.best_for || []).slice(0, 3).join(' · ') || c.currency}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Annual fee</div>
                          <div className="font-mono-num text-sm font-medium">{formatINR(c.annual_fee)}</div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                      </motion.button>
                    );
                  })}
                  {filteredCards.length === 0 && (
                    <div className="py-10 text-center text-sm text-muted-foreground">No cards match this search.</div>
                  )}
                </div>
              )}
            </Card>
          </motion.div>
        )}

        {/* STEP 3: Auto-populated preview + user-only fields */}
        {step === 3 && card && (
          <motion.div key="step3" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: 0.2 }}>
            <div className="grid lg:grid-cols-5 gap-5">
              {/* LEFT — auto-filled canonical data */}
              <div className="lg:col-span-3 space-y-4">
                <Card className="p-5">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => goToStep(2)} className="gap-1.5 -ml-2"><ChevronLeft className="h-3.5 w-3.5" /> Cards</Button>
                      <Separator orientation="vertical" className="h-5" />
                      <div className="text-sm font-medium">{card.bank} — {card.name}</div>
                    </div>
                    <Badge variant="outline" className="gap-1.5 text-emerald-500 border-emerald-500/30 bg-emerald-500/5"><Check className="h-3 w-3" /> Auto-filled from library</Badge>
                  </div>

                  <div className="mt-5 grid sm:grid-cols-2 gap-4 items-start">
                    <CreditCardVisual card={{ ...card, last4: last4 && /^\d{4}$/.test(last4) ? last4 : (card.last4 || '0000') }} size="md" />
                    <div className="text-sm space-y-2.5">
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Network</span><span className="font-medium">{card.network}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Tier</span><span className="font-medium">{card.tier}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Currency</span><span className="font-medium">{card.currency}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Pt value (INR)</span><span className="font-mono-num font-medium">{`₹${(card.point_value_inr || 1).toFixed(2)}`}</span></div>
                      <Separator />
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Annual fee</span><span className="font-mono-num font-medium">{formatINR(card.annual_fee)}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Fee waiver spend</span><span className="font-mono-num font-medium">{card.fee_waiver_inr ? formatINR(card.fee_waiver_inr) : 'None'}</span></div>
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">Welcome bonus</span><span className="font-medium text-right">{card.welcome_bonus || '—'}</span></div>
                    </div>
                  </div>
                </Card>

                <Card className="p-5">
                  <div className="flex items-center gap-2 text-sm font-medium"><Sparkles className="h-4 w-4 text-accent" /> Reward rules ({(card.rules || []).length}) <Badge variant="outline" className="text-[10px] ml-1">Read-only</Badge></div>
                  <div className="mt-3 divide-y divide-border">
                    {(card.rules || []).map((r) => {
                      const cats = (r.match?.categories || []).join(', ') || '—';
                      const mccs = (r.match?.mcc || []).slice(0, 4).join(', ');
                      return (
                        <div key={r.id} className="py-2.5 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm font-medium flex items-center gap-2 flex-wrap">{r.label}{r.exclude && <Badge variant="outline" className="text-rose-500 border-rose-500/30 bg-rose-500/5 text-[10px]">Excluded</Badge>}{r.cap?.amount_inr && <Badge variant="outline" className="text-[10px]">Cap {formatINR(r.cap.amount_inr, { compact: true })}</Badge>}</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{cats}{mccs && ` · MCC: ${mccs}`}</div>
                          </div>
                          <div className="text-right shrink-0">
                            {r.earn ? (
                              <Badge className="bg-primary/10 text-primary border-primary/20">{r.earn.type === 'percent_cashback' ? `${r.earn.rate}%` : `${r.earn.rate}x`}</Badge>
                            ) : <Badge variant="outline">—</Badge>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {(card.milestones || []).length > 0 && (
                  <Card className="p-5">
                    <div className="flex items-center gap-2 text-sm font-medium"><Trophy className="h-4 w-4 text-accent" /> Milestones <Badge variant="outline" className="text-[10px] ml-1">Read-only</Badge></div>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      {card.milestones.map((m) => (
                        <div key={m.id} className="rounded-lg border border-border bg-[hsl(var(--surface-2))] p-3">
                          <div className="font-display text-sm font-semibold">{m.name}</div>
                          <div className="mt-1 text-xs text-muted-foreground">{m.reward}</div>
                          <div className="mt-2 flex items-center justify-between text-[11px]"><span className="text-muted-foreground">Target</span><span className="font-mono-num">{formatINR(m.target_inr, { compact: true })}</span></div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {(card.benefits || []).length > 0 && (
                  <Card className="p-5">
                    <div className="flex items-center gap-2 text-sm font-medium"><Wallet className="h-4 w-4 text-accent" /> Benefits</div>
                    <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                      {card.benefits.map((b) => (<li key={b} className="text-sm flex items-start gap-2"><Check className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" /> {b}</li>))}
                    </ul>
                  </Card>
                )}
              </div>

              {/* RIGHT — only user-owned fields */}
              <div className="lg:col-span-2 space-y-4 lg:sticky lg:top-20 self-start">
                <Card className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium flex items-center gap-2"><Lock className="h-4 w-4 text-accent" /> Your details</div>
                    <Badge variant="outline" className="text-[10px]">Optional</Badge>
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">These are the only fields you provide. Everything else is platform-owned.</p>

                  {alreadyOwned && (
                    <div className="mt-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-600 dark:text-amber-400 flex items-start gap-2">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> This card is already in your wallet.
                    </div>
                  )}

                  <div className="mt-4 space-y-3">
                    <div>
                      <Label className="text-xs">Nickname <span className="text-muted-foreground">(optional)</span></Label>
                      <Input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder={`e.g. "Travel card"`} className="h-10 mt-1" data-testid="addcard-nickname" />
                    </div>
                    <div>
                      <Label className="text-xs">
                        Approval / joining date <span className="text-muted-foreground">(optional)</span>
                      </Label>
                      <div className="relative mt-1">
                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          type="date"
                          value={approvedAt}
                          onChange={(e) => {
                            setApprovedAt(e.target.value);
                            if (e.target.value) setApprovedAtUnknown(false);
                          }}
                          disabled={approvedAtUnknown}
                          className="h-10 pl-10 disabled:opacity-60"
                          data-testid="addcard-approved-at"
                        />
                      </div>
                      <label className="mt-2 flex items-start gap-2 text-[11px] text-muted-foreground cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={approvedAtUnknown}
                          onChange={(e) => {
                            setApprovedAtUnknown(e.target.checked);
                            if (e.target.checked) setApprovedAt('');
                          }}
                          className="mt-0.5 h-3.5 w-3.5 accent-accent"
                          data-testid="addcard-approved-at-unknown"
                        />
                        <span>
                          Don&rsquo;t remember the exact date.
                          {(approvedAtUnknown || !approvedAt) && (
                            <span className="block text-amber-600 dark:text-amber-400 mt-0.5">
                              Anniversary milestones and welcome benefits will stay locked until you confirm a date — you can update it any time from the milestone card or card details.
                            </span>
                          )}
                        </span>
                      </label>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Credit limit (₹)</Label>
                        <Input value={creditLimit} onChange={(e) => setCreditLimit(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="500000" className="h-10 mt-1 font-mono-num" data-testid="addcard-credit-limit" />
                      </div>
                      <div>
                        <Label className="text-xs">Last 4 digits</Label>
                        <Input value={last4} onChange={(e) => setLast4(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="0000" className="h-10 mt-1 font-mono-num" data-testid="addcard-last4" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-border bg-[hsl(var(--surface-2))] px-3 py-2.5">
                      <div>
                        <Label htmlFor="active" className="text-sm font-medium">Active</Label>
                        <div className="text-[11px] text-muted-foreground">Toggle off if currently dormant or expired.</div>
                      </div>
                      <Switch id="active" checked={isActive} onCheckedChange={setIsActive} data-testid="addcard-is-active" />
                    </div>
                  </div>

                  <Separator className="my-5" />
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                    <ShieldCheck className="h-3 w-3 text-emerald-500" /> We never store full card numbers or CVV.
                  </div>
                </Card>

                {/* Phase 10.2 — "Current state" is now MANDATORY. Users
                    must either tick "Earned nothing yet" (fast path for new
                    cards) or fill at least the reward-balance field. The
                    section is auto-expanded so users can't miss it. */}
                <Card
                  className={cn(
                    'p-5',
                    !currentStateSatisfied && 'border-rose-500/40 ring-1 ring-rose-500/20',
                  )}
                  data-testid="addcard-current-state-card"
                >
                  <button
                    type="button"
                    onClick={() => setShowCurrentState((v) => !v)}
                    className="w-full flex items-center justify-between text-left"
                    data-testid="addcard-current-state-toggle"
                  >
                    <div>
                      <div className="text-sm font-medium flex items-center gap-2">
                        <Trophy className="h-4 w-4 text-accent" /> Current state on this card
                        <span className="text-rose-500">*</span>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        Required — tell us what you&rsquo;ve already earned so we don&rsquo;t re-promise it.
                      </div>
                    </div>
                    <ChevronDown className={cn('h-4 w-4 transition-transform', showCurrentState && 'rotate-180')} />
                  </button>

                  {showCurrentState && (
                    <div className="mt-4 space-y-3" data-testid="addcard-current-state-body">
                      <label
                        className={cn(
                          'flex items-start gap-2 rounded-md border px-3 py-2.5 cursor-pointer text-sm',
                          earnedNothingYet
                            ? 'border-emerald-500/40 bg-emerald-500/5'
                            : 'border-border hover:bg-[hsl(var(--surface-2))]',
                        )}
                        data-testid="addcard-earned-nothing-yet-label"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 accent-emerald-500"
                          checked={earnedNothingYet}
                          onChange={(e) => {
                            setEarnedNothingYet(e.target.checked);
                            if (e.target.checked) {
                              setCurrentRewardPoints('');
                              setAchievedMilestoneIds([]);
                              setAchievedTierId('');
                              setTierProgressInr('');
                            }
                          }}
                          data-testid="addcard-earned-nothing-yet"
                        />
                        <div className="flex-1">
                          <div className="font-medium">I&rsquo;ve earned nothing yet on this card</div>
                          <div className="text-[11px] text-muted-foreground mt-0.5">
                            Tick this if you just got the card (or you don&rsquo;t want to enter the details right now). You can always update later from Cards → Edit.
                          </div>
                        </div>
                      </label>

                      <div className={cn('space-y-3', earnedNothingYet && 'opacity-50 pointer-events-none')}>
                        <div className="relative pt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                          <span className="bg-card px-1.5 relative z-10">Or enter your current state</span>
                          <span className="absolute left-0 right-0 top-1/2 -translate-y-1/2 border-t border-border z-0" />
                        </div>

                        <div>
                          <Label className="text-xs">Current reward balance</Label>
                          <Input
                            value={currentRewardPoints}
                            onChange={(e) => setCurrentRewardPoints(e.target.value.replace(/[^0-9.]/g, ''))}
                            inputMode="numeric"
                            placeholder="e.g. 24500"
                            className="h-10 mt-1 font-mono-num"
                            data-testid="addcard-current-points"
                          />
                          <div className="text-[10px] text-muted-foreground mt-1">
                            In {card.currency} (≈ {`₹${(card.point_value_inr || 1).toFixed(2)}`} per point)
                          </div>
                        </div>

                      {(card.milestones || []).length > 0 && (
                        <div>
                          <Label className="text-xs">Milestones you've already received</Label>
                          <div className="mt-1 space-y-1.5">
                            {card.milestones.map((m) => {
                              const checked = achievedMilestoneIds.includes(m.id);
                              return (
                                <label
                                  key={m.id}
                                  className={cn(
                                    'flex items-start gap-2 rounded-md border px-2.5 py-2 cursor-pointer text-xs',
                                    checked ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-border hover:bg-[hsl(var(--surface-2))]'
                                  )}
                                  data-testid={`addcard-milestone-${m.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    className="mt-0.5 h-3.5 w-3.5 accent-emerald-500"
                                    checked={checked}
                                    onChange={(e) => {
                                      setAchievedMilestoneIds((prev) =>
                                        e.target.checked ? [...prev, m.id] : prev.filter((x) => x !== m.id)
                                      );
                                    }}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium">{m.name}</div>
                                    <div className="text-[10px] text-muted-foreground truncate">
                                      {m.reward} · target {formatINR(m.target_inr, { compact: true })} · {m.period || 'anniversary'}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                          <div className="text-[10px] text-muted-foreground mt-1.5">
                            Checked milestones will be marked "Already received" and won't be re-counted in recommendations.
                          </div>
                        </div>
                      )}

                      {(card.tiers || []).length > 0 && (
                        <>
                          <div>
                            <Label className="text-xs">Current tier</Label>
                            <div className="mt-1 grid gap-1.5">
                              <label
                                className={cn(
                                  'flex items-center justify-between rounded-md border px-2.5 py-2 cursor-pointer text-xs',
                                  achievedTierId === '' ? 'border-accent/40 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]'
                                )}
                              >
                                <span className="font-medium">None / base</span>
                                <input
                                  type="radio"
                                  name="addcard-tier"
                                  checked={achievedTierId === ''}
                                  onChange={() => setAchievedTierId('')}
                                  data-testid="addcard-tier-none"
                                />
                              </label>
                              {card.tiers.map((t) => (
                                <label
                                  key={t.id}
                                  className={cn(
                                    'flex items-center justify-between rounded-md border px-2.5 py-2 cursor-pointer text-xs',
                                    achievedTierId === t.id ? 'border-accent/40 bg-accent/5' : 'border-border hover:bg-[hsl(var(--surface-2))]'
                                  )}
                                  data-testid={`addcard-tier-${t.id}`}
                                >
                                  <div className="min-w-0 flex-1 truncate">
                                    <span className="font-medium">{t.name}</span>
                                    <span className="text-muted-foreground"> · {(t.rate_multiplier || 1).toFixed(1)}x · threshold {formatINR(t.threshold_inr || 0, { compact: true })}</span>
                                  </div>
                                  <input
                                    type="radio"
                                    name="addcard-tier"
                                    checked={achievedTierId === t.id}
                                    onChange={() => setAchievedTierId(t.id)}
                                  />
                                </label>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs">
                              Spend toward next tier this period <span className="text-muted-foreground">(optional)</span>
                            </Label>
                            <Input
                              value={tierProgressInr}
                              onChange={(e) => setTierProgressInr(e.target.value.replace(/[^0-9.]/g, ''))}
                              inputMode="numeric"
                              placeholder="e.g. 450000"
                              className="h-10 mt-1 font-mono-num"
                              data-testid="addcard-tier-progress"
                            />
                            <div className="text-[10px] text-muted-foreground mt-1">
                              In ₹. Helps the engine project when you'll cross the next tier.
                            </div>
                          </div>
                        </>
                      )}

                      {((card.milestones || []).length === 0 && (card.tiers || []).length === 0) && (
                        <div className="text-[11px] text-muted-foreground">
                          This card has no milestones or tiers — nothing to capture beyond your point balance.
                        </div>
                      )}
                      </div>  {/* /space-y-3 disabled when earnedNothingYet */}
                    </div>
                  )}
                </Card>

                {/* Phase 10.2 — Submission row moved to the bottom so users
                    are guaranteed to scroll past the mandatory current-state
                    panel before hitting Add. */}
                <Card className="p-4">
                  {submitBlockReason && (
                    <div className="mb-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[12px] text-amber-700 dark:text-amber-300 flex items-start gap-2" data-testid="addcard-submit-block-reason">
                      <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>{submitBlockReason}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => goToStep(2)} className="gap-1.5 flex-1"><ChevronLeft className="h-4 w-4" /> Back</Button>
                    <Button
                      onClick={submit}
                      disabled={saving || alreadyOwned || !canSubmit}
                      className="flex-1 gap-1.5 bg-accent text-accent-foreground hover:bg-accent/90 disabled:opacity-60"
                      data-testid="addcard-save-button"
                    >
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Add to wallet
                    </Button>
                  </div>
                </Card>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
